import { useCallback, useEffect, useId, useRef, useState, type InputHTMLAttributes, type Ref } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import {
  autocompletePlaces,
  newPlacesSession,
  placeDetails,
  placesEnabled,
  type PlaceAddress,
  type PlaceSuggestion,
} from '../../services/places';

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => unknown;
  ref?: Ref<HTMLInputElement>;
};

interface AddressAutocompleteProps {
  /**
   * Everything the plain input would have had — a react-hook-form
   * `register(...)` result spreads straight in. The field stays an ordinary
   * input: with lookup unavailable it is exactly what it was before.
   */
  inputProps: InputProps;
  onPick: (address: PlaceAddress) => void;
  className?: string;
  placeholder?: string;
}

const DEBOUNCE_MS = 300;

/**
 * A street-address input that suggests Australian addresses as you type and,
 * on a pick, hands back the address split into parts for the caller to fill
 * its other fields with. Suggestions come from Google Places through our own
 * backend, so no key is ever in the bundle.
 */
function AddressAutocomplete({ inputProps, onPick, className, placeholder }: AddressAutocompleteProps) {
  const { ref: outerRef, onChange: outerOnChange, onKeyDown: outerOnKeyDown, onBlur: outerOnBlur, ...rest } = inputProps;
  const listId = useId();
  const [enabled, setEnabled] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const session = useRef<string>(newPlacesSession());
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    placesEnabled().then((on) => { if (!cancelled) setEnabled(on); });
    return () => { cancelled = true; };
  }, []);

  // Only text the person typed is looked up — never a value the form set.
  useEffect(() => {
    const q = query.trim();
    if (!enabled || q.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      autocompletePlaces(q, session.current)
        .then((list) => {
          if (cancelled) return;
          setSuggestions(list);
          setActive(-1);
          setOpen(list.length > 0);
        })
        .catch(() => { if (!cancelled) setSuggestions([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query, enabled]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (container.current && !container.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const pick = useCallback(async (s: PlaceSuggestion) => {
    setOpen(false);
    setPicking(true);
    try {
      const address = await placeDetails(s.place_id, session.current);
      if (address) onPick(address);
    } catch {
      // Leave the typed text in place; the fields can still be filled by hand.
    } finally {
      setPicking(false);
      setQuery('');
      setSuggestions([]);
      // The pick ends Google's billing session; the next address starts a new one.
      session.current = newPlacesSession();
    }
  }, [onPick]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (open && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        return;
      }
      if (e.key === 'Enter' && active >= 0) {
        e.preventDefault();
        void pick(suggestions[active]);
        return;
      }
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
    }
    outerOnKeyDown?.(e);
  };

  const showList = enabled && open && suggestions.length > 0;

  return (
    <div ref={container} className="relative">
      <div className="relative">
        <input
          {...rest}
          ref={outerRef}
          type="text"
          role={enabled ? 'combobox' : undefined}
          aria-autocomplete={enabled ? 'list' : undefined}
          aria-expanded={enabled ? showList : undefined}
          aria-controls={enabled ? listId : undefined}
          aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete={enabled ? 'off' : rest.autoComplete}
          placeholder={placeholder ?? rest.placeholder}
          className={`${className ?? ''} ${enabled ? 'pr-9' : ''}`}
          onChange={(e) => {
            outerOnChange?.(e);
            setQuery(e.target.value);
          }}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onKeyDown={onKeyDown}
          onBlur={outerOnBlur}
        />
        {enabled && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400">
            {loading || picking ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
          </span>
        )}
      </div>
      {showList && (
        <div className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg">
          <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto">
            {suggestions.map((s, i) => (
              <li
                key={s.place_id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                // mousedown, not click: the input's blur would otherwise
                // close the list before the click lands.
                onMouseDown={(e) => { e.preventDefault(); void pick(s); }}
                onMouseEnter={() => setActive(i)}
                className={`cursor-pointer border-b border-neutral-100 px-3 py-2 text-sm last:border-b-0 ${i === active ? 'bg-primary-50' : ''}`}
              >
                <span className="block break-words text-neutral-800">{s.main}</span>
                {s.secondary && <span className="block break-words text-xs text-neutral-500">{s.secondary}</span>}
              </li>
            ))}
          </ul>
          {/* Google's terms require attribution on Places results shown without a Google map. */}
          <p className="border-t border-neutral-100 bg-neutral-50 px-3 py-1 text-right text-[11px] text-neutral-500">
            Powered by Google
          </p>
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
